/**
 * GameSession Service
 * 
 * Client-side session state management for multiplayer games.
 * Coordinates with NetworkSync for WebSocket communication and maintains local session data.
 */

import { NetworkSync } from './NetworkSync';
import type { EventSubscription } from './NetworkSync';

// Inline all necessary types to avoid module resolution issues
interface SessionPlayer {
  playerId: string;
  sessionId: string;
  displayName: string;
  playerNumber: number | null;
  role: PlayerRole;
  connectionStatus: ConnectionStatus;
  joinedAt: Date;
  lastActivityAt: Date;
}

interface GameSettings {
  sessionId: string;
  spectatorCanSeeLetters: boolean;
  handicaps: Map<number, any>;
  turnTimeLimit: number;
  allowMidGameJoining: boolean;
}

const PlayerRole = { PLAYER: 'PLAYER', SPECTATOR: 'SPECTATOR' } as const;
type PlayerRole = typeof PlayerRole[keyof typeof PlayerRole];

const ConnectionStatus = { CONNECTED: 'CONNECTED', DISCONNECTED: 'DISCONNECTED', RECONNECTING: 'RECONNECTING' } as const;
type ConnectionStatus = typeof ConnectionStatus[keyof typeof ConnectionStatus];

const SessionPhase = { WAITING: 'WAITING', PLAYING: 'PLAYING', FINISHED: 'FINISHED' } as const;
type SessionPhase = typeof SessionPhase[keyof typeof SessionPhase];

type JoinError = 'INVALID_CODE' | 'SESSION_NOT_FOUND' | 'SESSION_FULL' | 'SESSION_EXPIRED' | 'ALREADY_IN_SESSION';

type PlayerLeaveReason = 'LEFT_VOLUNTARILY' | 'DISCONNECTED' | 'KICKED' | 'CONNECTION_LOST';

interface CreateSessionPayload {
  displayName: string;
  maxPlayers?: number;
  settings?: any;
}

interface JoinSessionPayload {
  shareCode: string;
  displayName: string;
}

interface SessionCreatedPayload {
  sessionId: string;
  shareCode: string;
  playerNumber: number;
  role: PlayerRole;
  settings: any;
  phase: SessionPhase;
  players: SessionPlayer[];
  boardSeed?: string;
}

interface SessionJoinedPayload {
  sessionId: string;
  shareCode: string;
  playerNumber: number;
  role: PlayerRole;
  phase: SessionPhase;
  gameState: any;
  players: SessionPlayer[];
}

interface JoinErrorPayload {
  error: JoinError;
  message: string;
}

interface PlayerJoinedPayload {
  player: SessionPlayer;
  totalPlayers: number;
  totalSpectators: number;
}

interface PlayerLeftPayload {
  playerId: string;
  displayName: string;
  reason: PlayerLeaveReason;
  remainingPlayers: SessionPlayer[];
}

export interface SessionState {
  sessionId: string | null;
  shareCode: string | null;
  isMultiplayer: boolean;
  phase: SessionPhase;
  role: PlayerRole | null;
  playerNumber: number | null;
  players: SessionPlayer[];
  settings: GameSettings | null;
  maxPlayers: number;
  isCreator: boolean;
  gameState: any; // Will be typed properly when integrated with game
}

export type BoardSyncGuardStatus = 'ready' | 'waiting' | 'blocked';

export interface BoardSyncGuardState {
  sessionId: string | null;
  localBoardRevision: number;
  requiredBoardRevision: number | null;
  syncStatus: BoardSyncGuardStatus;
  waitStartedAt: string | null;
  waitTimeoutMs: number;
  blockingReason: string | null;
}

export interface CreateSessionOptions {
  displayName: string;
  maxPlayers?: number;
  settings?: Partial<GameSettings>;
}

export interface JoinSessionOptions {
  shareCode: string;
  displayName: string;
}

export type SessionEventCallback = {
  onSessionCreated?: (data: SessionCreatedPayload) => void;
  onSessionJoined?: (data: SessionJoinedPayload) => void;
  onJoinError?: (data: JoinErrorPayload) => void;
  onPlayerJoined?: (data: PlayerJoinedPayload) => void;
  onPlayerLeft?: (data: PlayerLeftPayload) => void;
  onSessionEnded?: () => void;
  onConnectionLost?: () => void;
  onConnectionRestored?: () => void;
  onRestoreFailed?: (message: string) => void;
  onGameAction?: (actionType: string, actionData: any) => void;
  onGameStarted?: (data: any) => void;
};

export class GameSession {
  private networkSync: NetworkSync;
  private sessionState: SessionState;
  private eventSubscriptions: EventSubscription[] = [];
  private callbacks: SessionEventCallback = {};
  private connectionCheckTimer: NodeJS.Timeout | null = null;
  private boardSyncGuardTimeout: NodeJS.Timeout | null = null;
  private boardSyncGuardState: BoardSyncGuardState;
  private lastBoardSyncError: {
    source: string;
    incomingBoardRevision: number;
    currentBoardRevision: number;
    timestamp: string;
  } | null = null;
  private pendingBoardEntryRevision: number | null = null;

  constructor(networkSync?: NetworkSync) {
    this.networkSync = networkSync || NetworkSync.getInstance();
    
    this.sessionState = {
      sessionId: null,
      shareCode: null,
      isMultiplayer: false,
      phase: SessionPhase.WAITING,
      role: null,
      playerNumber: null,
      players: [],
      settings: null,
      maxPlayers: 4,
      isCreator: false,
      gameState: {}
    };

    this.boardSyncGuardState = {
      sessionId: null,
      localBoardRevision: -1,
      requiredBoardRevision: null,
      syncStatus: 'ready',
      waitStartedAt: null,
      waitTimeoutMs: 3000,
      blockingReason: null,
    };

    this.setupNetworkEventHandlers();
    this.startConnectionMonitoring();
  }

  // ==================== Network Event Setup ====================

  private setupNetworkEventHandlers(): void {
    // Session creation response
    this.eventSubscriptions.push(
      this.networkSync.on('session_created', (payload) => {
        this.handleSessionCreated(payload);
      })
    );

    // Session join response
    this.eventSubscriptions.push(
      this.networkSync.on('session_joined', (payload) => {
        this.handleSessionJoined(payload);
      })
    );

    // Join error response
    this.eventSubscriptions.push(
      this.networkSync.on('join_error', (payload) => {
        this.handleJoinError(payload);
      })
    );

    this.eventSubscriptions.push(
      this.networkSync.on('player_joined', (payload) => {
        this.handlePlayerJoined(payload as PlayerJoinedPayload);
      })
    );

    this.eventSubscriptions.push(
      this.networkSync.on('player_left', (payload) => {
        this.handlePlayerLeft(payload as PlayerLeftPayload);
      })
    );

    this.eventSubscriptions.push(
      this.networkSync.on('game_started', (payload) => {
        this.handleGameStarted(payload);
      })
    );

    this.eventSubscriptions.push(
      this.networkSync.on('session_restored', (payload) => {
        this.handleSessionRestored(payload);
      })
    );

    this.eventSubscriptions.push(
      this.networkSync.on('restore_failed', (payload) => {
        this.handleRestoreFailed(payload);
      })
    );

    this.eventSubscriptions.push(
      this.networkSync.on('player_reconnected', (payload) => {
        this.handlePlayerReconnected(payload);
      })
    );

    // Game action events
    this.eventSubscriptions.push(
      this.networkSync.on('game_action', (payload) => {
        this.handleGameAction(payload);
      })
    );

    // Keep local session game state in sync with authoritative server updates.
    this.eventSubscriptions.push(
      this.networkSync.on('game_state_update', (payload) => {
        this.handleGameStateUpdate(payload);
      })
    );

    this.eventSubscriptions.push(
      this.networkSync.on('turn_change', (payload) => {
        this.handleTurnChange(payload);
      })
    );

    // Connection events
    this.eventSubscriptions.push(
      this.networkSync.on('disconnect', () => {
        this.handleConnectionLost();
      })
    );

    this.eventSubscriptions.push(
      this.networkSync.on('connection_ack', () => {
        this.handleConnectionRestored();
      })
    );
  }

  // ==================== Session Management ====================

  public async createSession(options: CreateSessionOptions): Promise<void> {
    if (this.sessionState.isMultiplayer) {
      throw new Error('Already in a multiplayer session');
    }

    try {
      const payload: CreateSessionPayload = {
        displayName: options.displayName,
        maxPlayers: options.maxPlayers || 4,
        settings: options.settings
      };

      // Ensure network connection
      if (!this.networkSync.isConnected()) {
        await this.networkSync.connect();
      }

      await this.networkSync.createSession(payload);
      console.log('[GameSession] Session creation request sent');

    } catch (error) {
      console.error('[GameSession] Failed to create session:', error);
      throw error;
    }
  }

  public async joinSession(options: JoinSessionOptions): Promise<void> {
    if (this.sessionState.isMultiplayer) {
      throw new Error('Already in a multiplayer session');
    }

    try {
      const payload: JoinSessionPayload = {
        shareCode: options.shareCode.toUpperCase(),
        displayName: options.displayName
      };

      // Ensure network connection
      if (!this.networkSync.isConnected()) {
        await this.networkSync.connect();
      }

      await this.networkSync.joinSession(payload);
      console.log('[GameSession] Session join request sent');

    } catch (error) {
      console.error('[GameSession] Failed to join session:', error);  
      throw error;
    }
  }

  public leaveSession(): void {
    if (!this.sessionState.isMultiplayer) {
      console.warn('[GameSession] Not in a multiplayer session');
      return;
    }

    this.networkSync.leaveSession();
    this.resetSessionState();
    
    if (this.callbacks.onSessionEnded) {
      this.callbacks.onSessionEnded();
    }

    console.log('[GameSession] Left session');
  }

  // ==================== Event Handlers ====================

  private normalizePlayer(raw: any, fallbackSessionId: string): SessionPlayer | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    if (typeof raw.playerId !== 'string' || raw.playerId.trim().length === 0) {
      return null;
    }

    const role = raw.role === PlayerRole.SPECTATOR ? PlayerRole.SPECTATOR : PlayerRole.PLAYER;
    const connectionStatus =
      raw.connectionStatus === ConnectionStatus.DISCONNECTED ||
      raw.connectionStatus === ConnectionStatus.RECONNECTING
        ? raw.connectionStatus
        : ConnectionStatus.CONNECTED;

    const parseDate = (value: any): Date => {
      const parsed = value ? new Date(value) : new Date();
      return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    };

    return {
      playerId: raw.playerId,
      sessionId:
        typeof raw.sessionId === 'string' && raw.sessionId.trim().length > 0
          ? raw.sessionId
          : fallbackSessionId,
      displayName:
        typeof raw.displayName === 'string' && raw.displayName.trim().length > 0
          ? raw.displayName.trim()
          : 'Player',
      playerNumber: typeof raw.playerNumber === 'number' ? raw.playerNumber : null,
      role,
      connectionStatus,
      joinedAt: parseDate(raw.joinedAt),
      lastActivityAt: parseDate(raw.lastActivityAt)
    };
  }

  private normalizePlayers(rawPlayers: any, fallbackSessionId: string): SessionPlayer[] {
    if (!Array.isArray(rawPlayers)) {
      return [];
    }

    const deduped = new Map<string, SessionPlayer>();
    for (const rawPlayer of rawPlayers) {
      const normalized = this.normalizePlayer(rawPlayer, fallbackSessionId);
      if (normalized) {
        deduped.set(normalized.playerId, normalized);
      }
    }

    return Array.from(deduped.values()).sort((a, b) => {
      if (a.playerNumber === null) return 1;
      if (b.playerNumber === null) return -1;
      return a.playerNumber - b.playerNumber;
    });
  }

  private handleSessionCreated(payload: SessionCreatedPayload): void {
    console.log('[GameSession] Session created:', payload.shareCode);

    this.sessionState.sessionId = payload.sessionId;
    this.sessionState.shareCode = payload.shareCode;
    this.sessionState.isMultiplayer = true;
    this.sessionState.phase = payload.phase ?? SessionPhase.WAITING;
    this.sessionState.role = payload.role ?? PlayerRole.PLAYER;
    this.sessionState.playerNumber = payload.playerNumber;
    this.sessionState.settings = payload.settings;
    this.sessionState.isCreator = true;
    this.sessionState.players = this.normalizePlayers(payload.players ?? [], payload.sessionId);
    if (payload.boardSeed) {
      this.sessionState.gameState = { ...this.sessionState.gameState, boardSeed: payload.boardSeed };
    }

    if (this.callbacks.onSessionCreated) {
      this.callbacks.onSessionCreated(payload);
    }
  }

  private handleSessionJoined(payload: SessionJoinedPayload): void {
    if (!payload || typeof payload.sessionId !== 'string' || payload.sessionId.length === 0) {
      console.warn('[GameSession] Ignoring malformed session_joined payload');
      return;
    }

    console.log('[GameSession] Joined session:', payload.sessionId);

    const normalizedPlayers = this.normalizePlayers(payload.players, payload.sessionId);
    
    this.sessionState.sessionId = payload.sessionId;
    this.sessionState.shareCode = payload.shareCode || this.sessionState.shareCode;
    this.sessionState.isMultiplayer = true;
    this.sessionState.phase = payload.phase ?? (payload.gameState ? SessionPhase.PLAYING : SessionPhase.WAITING);
    this.sessionState.role = payload.role;
    this.sessionState.playerNumber = payload.playerNumber;
    this.sessionState.players = normalizedPlayers;
    const joinedGameState = payload.gameState || {};
    this.sessionState.gameState = {
      ...joinedGameState,
      wordBoardSnapshot: this.resolveWordBoardSnapshot(joinedGameState.wordBoardSnapshot, {
        source: 'session_joined',
        forceIncoming: true,
      }),
    };
    this.sessionState.isCreator = payload.playerNumber === 1;
    this.resetBoardSyncGuardFromCurrentSnapshot();

    if (this.callbacks.onSessionJoined) {
      this.callbacks.onSessionJoined({
        ...payload,
        players: normalizedPlayers
      });
    }
  }

  private handleJoinError(payload: JoinErrorPayload): void {
    console.warn('[GameSession] Join error:', payload.message);
    
    if (this.callbacks.onJoinError) {
      this.callbacks.onJoinError(payload);
    }
  }

  private handlePlayerJoined(payload: PlayerJoinedPayload): void {
    if (!payload || !this.sessionState.sessionId) {
      return;
    }

    const normalizedPlayer = this.normalizePlayer(payload.player, this.sessionState.sessionId);
    if (!normalizedPlayer) {
      console.warn('[GameSession] Ignoring malformed player_joined payload');
      return;
    }

    console.log('[GameSession] Player joined:', normalizedPlayer.displayName);

    this.addPlayerToState(normalizedPlayer);
    
    if (this.callbacks.onPlayerJoined) {
      this.callbacks.onPlayerJoined({
        ...payload,
        player: normalizedPlayer
      });
    }
  }

  private handlePlayerLeft(payload: PlayerLeftPayload): void {
    if (!payload || typeof payload.playerId !== 'string' || payload.playerId.length === 0) {
      console.warn('[GameSession] Ignoring malformed player_left payload');
      return;
    }

    console.log('[GameSession] Player left:', payload.displayName || payload.playerId);

    if (Array.isArray(payload.remainingPlayers) && this.sessionState.sessionId) {
      // Prefer server-authoritative player list when provided.
      this.sessionState.players = this.normalizePlayers(payload.remainingPlayers, this.sessionState.sessionId);
    } else {
      this.removePlayerFromState(payload.playerId);
    }
    
    if (this.callbacks.onPlayerLeft) {
      this.callbacks.onPlayerLeft(payload);
    }
  }

  private handleGameStarted(payload: any): void {
    console.log('[GameSession] Game started');
    
    // Update session state to reflect game has started
    this.sessionState.phase = SessionPhase.PLAYING;
    
    if (this.callbacks.onGameStarted) {
      this.callbacks.onGameStarted(payload);
    }
  }

  private handleSessionRestored(payload: any): void {
    console.log('[GameSession] Session restored:', payload.sessionId);

    const wasAlreadyInSession = this.sessionState.isMultiplayer;

    // Restore full session state
    this.sessionState.sessionId = payload.sessionId;
    this.sessionState.shareCode = payload.shareCode;
    this.sessionState.isMultiplayer = true;
    this.sessionState.phase = payload.phase || SessionPhase.WAITING;
    this.sessionState.role = payload.role;
    this.sessionState.playerNumber = payload.playerNumber;
    this.sessionState.players = payload.players;
    this.sessionState.gameState = {
      ...(payload.gameState || {}),
      wordBoardSnapshot: this.resolveWordBoardSnapshot(payload.gameState?.wordBoardSnapshot, {
        source: 'session_restored',
        forceIncoming: true,
      }),
    };
    this.sessionState.isCreator = payload.playerNumber === 1;
    this.resetBoardSyncGuardFromCurrentSnapshot();

    if (wasAlreadyInSession) {
      // Mid-game reconnection: session state is up to date; notify connection restored
      // so scenes can react without triggering a full game restart.
      if (this.callbacks.onConnectionRestored) {
        this.callbacks.onConnectionRestored();
      }
    } else {
      if (this.callbacks.onSessionJoined) {
        this.callbacks.onSessionJoined(payload);
      }
    }
  }

  private handleRestoreFailed(payload: any): void {
    const message = payload?.message ?? 'Session could not be restored.';
    console.warn('[GameSession] Session restore failed:', message);
    this.sessionState.isMultiplayer = false;
    if (this.callbacks.onRestoreFailed) {
      this.callbacks.onRestoreFailed(message);
    }
  }

  private handlePlayerReconnected(payload: any): void {
    if (!payload || !this.sessionState.sessionId) {
      return;
    }

    const normalizedPlayer = this.normalizePlayer(
      { ...payload, connectionStatus: ConnectionStatus.CONNECTED },
      this.sessionState.sessionId
    );
    if (!normalizedPlayer) {
      console.warn('[GameSession] Ignoring malformed player_reconnected payload');
      return;
    }

    console.log('[GameSession] Player reconnected:', normalizedPlayer.displayName);

    this.addPlayerToState(normalizedPlayer);

    const totalPlayers = this.sessionState.players.filter(p => p.role === PlayerRole.PLAYER).length;
    const totalSpectators = this.sessionState.players.filter(p => p.role === PlayerRole.SPECTATOR).length;

    if (this.callbacks.onPlayerJoined) {
      this.callbacks.onPlayerJoined({ player: normalizedPlayer, totalPlayers, totalSpectators });
    }
  }

  private handleConnectionLost(): void {
    console.warn('[GameSession] Connection lost');
    
    if (this.callbacks.onConnectionLost) {
      this.callbacks.onConnectionLost();
    }
  }

  private handleConnectionRestored(): void {
    console.log('[GameSession] Connection restored');

    // Re-join the session room so the server can route events to this socket.
    // Only sent when we were already in an active session before the reconnect.
    if (this.sessionState.sessionId && this.sessionState.isMultiplayer) {
      const myPlayer = this.sessionState.players.find(
        p => p.playerNumber === this.sessionState.playerNumber
      );
      const displayName = myPlayer?.displayName;
      if (displayName) {
        console.log('[GameSession] Re-joining session room after reconnect');
        this.networkSync.restoreSession({
          sessionId: this.sessionState.sessionId,
          displayName,
        });
      }
    }

    if (this.callbacks.onConnectionRestored) {
      this.callbacks.onConnectionRestored();
    }
  }

  private handleGameAction(payload: any): void {
    console.log('[GameSession] Game action received:', payload.actionType);
    
    // Forward to registered callback (like PoolScene)
    if (this.callbacks.onGameAction) {
      this.callbacks.onGameAction(payload.actionType, payload.actionData);
    }
  }

  private getSnapshotVersion(snapshot: any): number {
    if (!snapshot || typeof snapshot !== 'object') {
      return -1;
    }

    const boardRevision = (snapshot as any).boardRevision;
    return typeof boardRevision === 'number' && Number.isFinite(boardRevision) ? boardRevision : -1;
  }

  private reportBoardSyncIntegrityError(source: string, incomingBoardRevision: number, currentBoardRevision: number): void {
    this.lastBoardSyncError = {
      source,
      incomingBoardRevision,
      currentBoardRevision,
      timestamp: new Date().toISOString(),
    };

    console.error(
      `[GameSession] BOARD INTEGRITY ERROR (${source}) incoming boardRevision=${incomingBoardRevision} ` +
      `is not greater than current boardRevision=${currentBoardRevision}. Keeping current snapshot.`
    );
  }

  private resolveWordBoardSnapshot(
    incomingSnapshot: any,
    options: { source?: string; forceIncoming?: boolean } = {}
  ): any {
    const currentSnapshot = this.sessionState.gameState?.wordBoardSnapshot;

    if (!incomingSnapshot) {
      return currentSnapshot;
    }

    if (options.forceIncoming || !currentSnapshot) {
      return incomingSnapshot;
    }

    const incomingVersion = this.getSnapshotVersion(incomingSnapshot);
    const currentVersion = this.getSnapshotVersion(currentSnapshot);

    if (incomingVersion > currentVersion) {
      return incomingSnapshot;
    }

    if (incomingVersion === currentVersion) {
      // Equal revisions are expected for repeated turn/game updates in a steady state.
      // Keep current snapshot to avoid unnecessary churn and false integrity alarms.
      return currentSnapshot;
    }

    this.reportBoardSyncIntegrityError(options.source ?? 'snapshot_merge', incomingVersion, currentVersion);
    return currentSnapshot;
  }

  private handleGameStateUpdate(payload: any): void {
    if (!payload || typeof payload !== 'object') {
      return;
    }

    const incomingState = payload.gameState && typeof payload.gameState === 'object'
      ? payload.gameState
      : {};

    const authoritativeSnapshot = this.resolveWordBoardSnapshot(
      incomingState.wordBoardSnapshot ?? payload.wordBoardSnapshot,
      { source: 'game_state_update' }
    );

    const incomingCurrentPlayer = incomingState.currentPlayerNumber ?? incomingState.currentPlayer;
    const normalizedCurrentPlayer =
      typeof incomingCurrentPlayer === 'number' ? incomingCurrentPlayer : undefined;

    this.sessionState.gameState = {
      ...this.sessionState.gameState,
      ...incomingState,
      ...(normalizedCurrentPlayer !== undefined
        ? {
            currentPlayerNumber: normalizedCurrentPlayer,
          }
        : {}),
      wordBoardSnapshot: authoritativeSnapshot,
    };

    this.updateBoardSyncGuardFromSnapshot(authoritativeSnapshot);

    if (!Array.isArray(this.sessionState.gameState.collectedLetters)) {
      this.sessionState.gameState.collectedLetters = [];
    }
  }

  private handleTurnChange(payload: any): void {
    const currentPlayerNumber = payload?.currentPlayerNumber ?? payload?.nextPlayerNumber;
    if (typeof currentPlayerNumber !== 'number') {
      return;
    }

    const previousCurrentPlayerNumber = this.sessionState.gameState?.currentPlayerNumber
      ?? this.sessionState.gameState?.currentPlayer
      ?? null;
    const previousBoardRevision = this.getSnapshotVersion(this.sessionState.gameState?.wordBoardSnapshot);

    const turnChangeRack = Array.isArray(payload?.currentRackLetters)
      ? payload.currentRackLetters
      : this.sessionState.gameState?.currentRackLetters;
    const turnChangePlayerHands = payload?.playerHands && typeof payload.playerHands === 'object'
      ? payload.playerHands
      : this.sessionState.gameState?.playerHands;
    const turnChangePlayerScores = payload?.playerScores && typeof payload.playerScores === 'object'
      ? payload.playerScores
      : this.sessionState.gameState?.playerScores;
    const turnChangeLetterPool = payload?.letterPool && typeof payload.letterPool === 'object'
      ? payload.letterPool
      : this.sessionState.gameState?.letterPool;
    const turnChangeConsecutivePasses = payload?.consecutivePassesPerPlayer && typeof payload.consecutivePassesPerPlayer === 'object'
      ? payload.consecutivePassesPerPlayer
      : this.sessionState.gameState?.consecutivePassesPerPlayer;
    const turnNumber =
      typeof payload?.turnNumber === 'number'
        ? payload.turnNumber
        : this.sessionState.gameState?.turn;
    const resolvedSnapshot = this.resolveWordBoardSnapshot(payload?.wordBoardSnapshot, {
      source: 'turn_change',
    });

    this.sessionState.gameState = {
      ...this.sessionState.gameState,
      currentPlayerNumber,
      ...(typeof turnNumber === 'number' ? { turn: turnNumber } : {}),
      currentRackLetters: turnChangeRack,
      playerHands: turnChangePlayerHands,
      playerScores: turnChangePlayerScores,
      wordBoardSnapshot: resolvedSnapshot,
      ...(turnChangeLetterPool !== undefined ? { letterPool: turnChangeLetterPool } : {}),
      ...(turnChangeConsecutivePasses !== undefined ? { consecutivePassesPerPlayer: turnChangeConsecutivePasses } : {}),
    };

    console.log('[GameSession] TURN_HANDOFF', {
      sessionId: this.sessionState.sessionId,
      previousCurrentPlayerNumber,
      currentPlayerNumber,
      turnNumber: typeof turnNumber === 'number' ? turnNumber : this.sessionState.gameState?.turn,
      reason: payload?.reason,
      phase: this.sessionState.phase,
    });

    const localPlayerNumber = this.sessionState.playerNumber;
    if (
      typeof localPlayerNumber === 'number' &&
      currentPlayerNumber === localPlayerNumber &&
      previousCurrentPlayerNumber !== localPlayerNumber
    ) {
      const resolvedRevision = this.getSnapshotVersion(resolvedSnapshot);
      this.pendingBoardEntryRevision = Math.max(previousBoardRevision + 1, resolvedRevision);
    } else if (currentPlayerNumber !== localPlayerNumber) {
      this.pendingBoardEntryRevision = null;
    }

    this.updateBoardSyncGuardFromSnapshot(this.sessionState.gameState.wordBoardSnapshot);
  }

  private clearBoardSyncGuardTimer(): void {
    if (this.boardSyncGuardTimeout) {
      clearTimeout(this.boardSyncGuardTimeout);
      this.boardSyncGuardTimeout = null;
    }
  }

  private resetBoardSyncGuardFromCurrentSnapshot(): void {
    const revision = this.getSnapshotVersion(this.sessionState.gameState?.wordBoardSnapshot);
    this.clearBoardSyncGuardTimer();
    this.pendingBoardEntryRevision = null;
    this.boardSyncGuardState = {
      sessionId: this.sessionState.sessionId,
      localBoardRevision: revision,
      requiredBoardRevision: null,
      syncStatus: 'ready',
      waitStartedAt: null,
      waitTimeoutMs: 3000,
      blockingReason: null,
    };
  }

  private updateBoardSyncGuardFromSnapshot(snapshot: any): void {
    const revision = this.getSnapshotVersion(snapshot);
    if (revision <= this.boardSyncGuardState.localBoardRevision) {
      return;
    }

    this.boardSyncGuardState.localBoardRevision = revision;
    if (this.pendingBoardEntryRevision !== null && revision >= this.pendingBoardEntryRevision) {
      this.pendingBoardEntryRevision = null;
    }
    if (
      this.boardSyncGuardState.syncStatus !== 'ready' &&
      this.boardSyncGuardState.requiredBoardRevision !== null &&
      revision >= this.boardSyncGuardState.requiredBoardRevision
    ) {
      this.clearBoardSyncGuardTimer();
      this.boardSyncGuardState.syncStatus = 'ready';
      this.boardSyncGuardState.requiredBoardRevision = null;
      this.boardSyncGuardState.waitStartedAt = null;
      this.boardSyncGuardState.blockingReason = null;
    }
  }

  public beginBoardEntrySyncGuard(): BoardSyncGuardState {
    const localBoardRevision = this.getSnapshotVersion(this.sessionState.gameState?.wordBoardSnapshot);
    const hasSnapshot = localBoardRevision >= 0;

    if (this.pendingBoardEntryRevision !== null && localBoardRevision >= this.pendingBoardEntryRevision) {
      this.pendingBoardEntryRevision = null;
    }

    const requiredBoardRevision = this.pendingBoardEntryRevision;
    const needsFreshSnapshot = requiredBoardRevision !== null && localBoardRevision < requiredBoardRevision;

    if (hasSnapshot && !needsFreshSnapshot) {
      this.boardSyncGuardState = {
        sessionId: this.sessionState.sessionId,
        localBoardRevision,
        requiredBoardRevision: null,
        syncStatus: 'ready',
        waitStartedAt: null,
        waitTimeoutMs: this.boardSyncGuardState.waitTimeoutMs,
        blockingReason: null,
      };
      return { ...this.boardSyncGuardState };
    }

    const reason = hasSnapshot ? 'STALE_SNAPSHOT' : 'MISSING_SNAPSHOT';

    this.clearBoardSyncGuardTimer();

    this.boardSyncGuardState = {
      ...this.boardSyncGuardState,
      sessionId: this.sessionState.sessionId,
      localBoardRevision,
      requiredBoardRevision: requiredBoardRevision ?? Math.max(localBoardRevision + 1, 0),
      syncStatus: 'waiting',
      waitStartedAt: new Date().toISOString(),
      blockingReason: null,
    };

    if (this.sessionState.sessionId) {
      this.networkSync.requestBoardResync?.({
        sessionId: this.sessionState.sessionId,
        lastKnownBoardRevision: Math.max(localBoardRevision, 0),
        reason,
      });
    }

    this.boardSyncGuardTimeout = setTimeout(() => {
      if (this.boardSyncGuardState.syncStatus === 'waiting') {
        this.boardSyncGuardState.syncStatus = 'blocked';
        this.boardSyncGuardState.blockingReason =
          'Unable to synchronize board state. Waiting for authoritative resync.';
      }
    }, this.boardSyncGuardState.waitTimeoutMs);

    return { ...this.boardSyncGuardState };
  }

  public getBoardSyncGuardState(): BoardSyncGuardState {
    return { ...this.boardSyncGuardState };
  }

  public isBoardInteractionBlocked(): boolean {
    return this.boardSyncGuardState.syncStatus !== 'ready';
  }

  // ==================== Player State Management ====================

  private addPlayerToState(player: SessionPlayer): void {
    const existingIndex = this.sessionState.players.findIndex(p => p.playerId === player.playerId);
    
    if (existingIndex >= 0) {
      this.sessionState.players[existingIndex] = player;
    } else {
      this.sessionState.players.push(player);
    }

    // Sort players by join order (player number)
    this.sessionState.players.sort((a, b) => {
      if (a.playerNumber === null) return 1;
      if (b.playerNumber === null) return -1;
      return a.playerNumber - b.playerNumber;
    });
  }

  private removePlayerFromState(playerId: string): void {
    this.sessionState.players = this.sessionState.players.filter(p => p.playerId !== playerId);
  }

  // ==================== Session State Access ====================

  public getSessionState(): SessionState {
    return { ...this.sessionState };
  }

  public isInSession(): boolean {
    return this.sessionState.isMultiplayer && this.sessionState.sessionId !== null;
  }

  public isCreator(): boolean {
    return this.sessionState.isCreator;
  }

  public isPlayer(): boolean {
    return this.sessionState.role === PlayerRole.PLAYER;
  }

  public isSpectator(): boolean {
    return this.sessionState.role === PlayerRole.SPECTATOR;
  }

  public getShareCode(): string | null {
    return this.sessionState.shareCode;
  }

  public getPlayerCount(): number {
    return this.sessionState.players.filter(p => p.role === PlayerRole.PLAYER).length;
  }

  public getSpectatorCount(): number {
    return this.sessionState.players.filter(p => p.role === PlayerRole.SPECTATOR).length;
  }

  public getConnectedPlayerCount(): number {
    return this.sessionState.players.filter(p => p.connectionStatus === ConnectionStatus.CONNECTED).length;
  }

  public getCurrentPlayer(): SessionPlayer | null {
    const connectionId = this.networkSync.getConnectionState().connectionId;
    return this.sessionState.players.find(p => p.playerId === connectionId) || null;
  }

  public getPlayerByNumber(playerNumber: number): SessionPlayer | null {
    return this.sessionState.players.find(p => p.playerNumber === playerNumber) || null;
  }

  // ==================== Connection Monitoring ====================

  private startConnectionMonitoring(): void {
    this.connectionCheckTimer = setInterval(() => {
      const isConnected = this.networkSync.isConnected();
      
      if (this.sessionState.isMultiplayer && !isConnected) {
        console.warn('[GameSession] Connection lost while in session');
        // The NetworkSync will handle reconnection automatically
      }
    }, 5000); // Check every 5 seconds
  }

  // ==================== Event Callbacks ====================

  public setCallbacks(callbacks: SessionEventCallback): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  public clearCallbacks(): void {
    this.callbacks = {};
  }

  // ==================== Session Reset ====================

  private resetSessionState(): void {
    this.sessionState = {
      sessionId: null,
      shareCode: null,
      isMultiplayer: false,
      phase: SessionPhase.WAITING,
      role: null,
      playerNumber: null,
      players: [],
      settings: null,
      maxPlayers: 4,
      isCreator: false,
      gameState: {}
    };
    this.resetBoardSyncGuardFromCurrentSnapshot();
  }

  // ==================== Game State Synchronization ====================

  public updateLocalGameState(gameState: any): void {
    this.sessionState.gameState = {
      ...this.sessionState.gameState,
      ...gameState,
      wordBoardSnapshot: this.resolveWordBoardSnapshot(gameState?.wordBoardSnapshot),
    };
    this.updateBoardSyncGuardFromSnapshot(this.sessionState.gameState.wordBoardSnapshot);
  }

  public getGameState(): any {
    return this.sessionState.gameState;
  }

  // ==================== Cleanup ====================

  public cleanup(): void {
    // Clear timers
    if (this.connectionCheckTimer) {
      clearInterval(this.connectionCheckTimer);
      this.connectionCheckTimer = null;
    }
    this.clearBoardSyncGuardTimer();

    // Unsubscribe from network events
    this.eventSubscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
    this.eventSubscriptions = [];

    // Leave session if in one
    if (this.sessionState.isMultiplayer) {
      this.leaveSession();
    }

    // Reset state
    this.resetSessionState();
    this.clearCallbacks();

    console.log('[GameSession] Cleanup completed');
  }

  // ==================== Debug & Statistics ====================

  public getSessionSummary(): string {
    if (!this.sessionState.isMultiplayer) {
      return 'Not in multiplayer session';
    }

    const role = this.sessionState.role === PlayerRole.PLAYER 
      ? `Player ${this.sessionState.playerNumber}` 
      : 'Spectator';
    
    const creatorStatus = this.sessionState.isCreator ? ' (Creator)' : '';
    
    return `${this.sessionState.shareCode} - ${role}${creatorStatus} - ${this.getPlayerCount()}/${this.sessionState.maxPlayers} players, ${this.getSpectatorCount()} spectators`;
  }

  public getDebugInfo() {
    return {
      sessionState: this.sessionState,
      networkStats: this.networkSync.getStats(),
      activeSubscriptions: this.eventSubscriptions.length,
      callbacks: Object.keys(this.callbacks),
      connectionStatus: this.networkSync.getConnectionState(),
      lastBoardSyncError: this.lastBoardSyncError,
    };
  }
}